import type { NavigateFunction } from "react-router-dom";
import type { TClickAction } from "action-view";

export type TCrudActionLabels = {
  createLabel?: string;
  viewLabel?: string;
  editLabel?: string;
  deleteLabel?: string;
  cloneLabel?: string;
};

export type TGetCrudActionsParams = {
  rowKey: string;
  basePath: string;
  labels: TCrudActionLabels;
  navigate: NavigateFunction;
  apiDelete?: (rowId: string) => Promise<void>;
};

export function getCrudActions<TItem>({
  basePath,
  navigate,
  labels,
  rowKey,
  apiDelete,
}: TGetCrudActionsParams) {
  // Top-level page actions
  const pageActions: TClickAction[] = [];

  if (labels.createLabel) {
    pageActions.push({
      label: labels.createLabel,
      type: "primary",
      onClick: () => navigate(`/${basePath}/create`),
    });
  }

  // Row-level actions
  const buildRowActions = (row: TItem): TClickAction[] => {
    const id = row[rowKey as keyof TItem];
    if (id == null) return [];

    const actions: TClickAction[] = [];

    if (labels.viewLabel) {
      actions.push({
        label: labels.viewLabel,
        type: "text",
        onClick: () => navigate(`/${basePath}/view/${id}`),
      });
    }

    if (labels.editLabel) {
      actions.push({
        label: labels.editLabel,
        type: "text",
        onClick: () => navigate(`/${basePath}/edit/${id}`),
      });
    }

    if (labels.cloneLabel) {
      actions.push({
        label: labels.cloneLabel,
        type: "text",
        onClick: () => navigate(`/${basePath}/create`, { state: { cloneId: id } }),
      });
    }

    if (labels.deleteLabel && apiDelete) {
      actions.push({
        label: labels.deleteLabel,
        type: "text",
        danger: true,
        onClick: async () => {
          try {
            await apiDelete(row[rowKey as keyof TItem] as string);
          } catch (err) {
            console.error("Delete failed:", err);
          }
        },
      });
    }

    return actions;
  };

  return { pageActions, buildRowActions };
}
